import axios from "@/hooks/axios/axios";
import type {
  IAllocation,
  IApiKeysResponse,
  IApiTokenResponse,
  IGetApiTokenParams,
  ISubscriptionOverviewResponse,
  IUserAllocationResponse,
  TeamActivityLogParams,
  TeamActivityLogResponse,
} from "../types/token.types";

// API Token Management
export const getApiToken = async (
  params: IGetApiTokenParams = {},
): Promise<IApiKeysResponse> => {
  const { page = 1, page_size = 10, active_only = true } = params;
  const { data } = await axios.get<IApiKeysResponse>(
    `/api-keys/get?page=${page}&page_size=${page_size}&active_only=${active_only}`,
  );
  return data;
};

export const addApiToken = async ({
  entity_type,
}: {
  entity_type: string;
}): Promise<IApiTokenResponse> => {
  const url = `/add_api_token?entity_type=${entity_type}`;
  const { data } = await axios.post(url);
  return data;
};

export const deleteApiToken = async ({
  user_uuid,
  token_id,
}: {
  user_uuid: string;
  token_id: string;
}): Promise<void> => {
  const { data } = await axios.delete(
    `/delete_api_token?token_id=${user_uuid}`,
    {
      data: [token_id],
    },
  );
  return data;
};

// Unified Token Management & Allocation Overview
export const getSubscriptionOverview =
  async (): Promise<ISubscriptionOverviewResponse> => {
    const { data } = await axios.get("/subscription/overview");
    return data;
  };

export const getUserAllocations = async (
  userId: string,
): Promise<IUserAllocationResponse> => {
  const { data } = await axios.get(`/allocations/user/${userId}`);
  return data;
};

export const saveUserAllocations = async (
  userId: string,
  allocations: {
    allocation_id: string;
    allocated_quota: number;
    allocated_rate_limit: number;
    enabled: boolean;
  }[],
): Promise<{
  message: string;
  stats: {
    updated: number;
    deactivated: number;
  };
  allocations: IAllocation[];
}> => {
  const { data } = await axios.post(
    `/allocations/user/${userId}/save`,
    allocations,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return data;
};

// Create new token allocation for user
export const createUserAllocation = async ({
  userId,
  parent_token_id,
  entity_type,
}: {
  userId: string;
  parent_token_id: string;
  entity_type: string;
}): Promise<{
  message: string;
  token_id: string;
  token: string;
  entity_type: string;
  parent_token_id: string;
  user_id: string;
  user_name: string;
  allocated_quota: number;
  allocated_rate_limit: number;
  enabled: boolean;
  note: string;
}> => {
  const { data } = await axios.post(`/allocations/user/${userId}/create`, {
    parent_token_id,
    entity_type,
  });
  return data;
};

// Delete user allocation
export const deleteUserAllocation = async ({
  userId,
  allocationId,
}: {
  userId: string;
  allocationId: string;
}): Promise<{
  message: string;
  allocation_id: string;
  user_id: string;
  user_name: string;
  allocated_quota: number;
  remaining_quota: number;
  quota_returned_to_parent: number;
  parent_token_id: string;
  note: string;
}> => {
  const { data } = await axios.delete(
    `/allocations/user/${userId}/token/${allocationId}`,
  );
  return data;
};

// Legacy - Team Members (will be removed)
export const getTeamMembers = async () => {
  const { data } = await axios.get("/allocations/manager/teams");
  return data;
};

// Deactivate an API key by ID (manager action)
export const deactivateApiKey = async (apiKeyId: string) => {
  const { data } = await axios.delete(`/api-keys/delete/${apiKeyId}`);
  return data;
};

// Create API Key for a subscription plan
export const createApiKey = async (planId: string) => {
  const { data } = await axios.post(`/api-keys/create?plan_id=${planId}`);
  return data;
};

// Team Activity Logs

export const getTeamActivityLogs = async (
  params: TeamActivityLogParams = {},
): Promise<TeamActivityLogResponse> => {
  const { page = 1, limit = 10 } = params;

  const { data } = await axios.get("/subscription/activity-logs", {
    params: {
      page,
      limit,
    },
  });

  return data;
};
